import React, { useRef, useState, useEffect, Suspense } from 'react';
import c from 'classnames';
import { PapiModuleCustombox } from '@wix/da-papi-types';
import CustomBoxViewer from '@wix/da-shared-react/pkg/DaEditorWrapper/Viewer/CustomBox';
import useModule, {
  useModuleVersion,
} from '@wix/da-gruser-shared/pkg/hooks/useModule';
import useModuleData from '@wix/da-gruser-shared/pkg/hooks/useModuleData';
import { useCurrentZoneName } from '@wix/da-gruser-shared/pkg/hooks/useZone';
import TextRenderer from '@wix/da-shared-react/pkg/TextRenderer';
import Background from '@wix/da-shared-react/pkg/Background';
import { useMeasuredCookie } from '@wix/da-shared-react/pkg/Measured/useMeasuredCookie';
import {
  BackgroundImageDisplayMode,
  BackgroundType,
} from '@wix/da-shared-react/pkg/Background/types';
import { getMeasuredCookieType } from './helpers';

import s from './Custom.scss';

export interface Props {
  moduleId: number;
  viewerKeySuffix?: string;
  noPadding?: boolean;
}

const Custom: React.FC<Props> = ({ moduleId, viewerKeySuffix, noPadding }) => {
  const zoneName = useCurrentZoneName();
  const module = useModule(moduleId);
  const { moduleData } = useModuleData<PapiModuleCustombox>(moduleId);
  const moduleVersion = useModuleVersion(moduleId);
  const {
    backgroundType,
    customtext,
    color,
    gradient,
    pattern,
    image: imageDeviation,
    imageDisplayMode,
    imageTint,
    imageUrl,
  } = moduleData;

  const backgroundProps = {
    imageDeviation,
    imageUrl,
    backgroundType: backgroundType as BackgroundType, // I <3 TS
    imageDisplayMode: imageDisplayMode as BackgroundImageDisplayMode,
    imageTint,
    pattern,
    gradient,
    color,
  };

  // Force measured cache bust any time viewerKeySuffix updates
  const [measuredVersion, setMeasuredVersion] = useState(0);
  const [viewerKey, setViewerKey] = useState(viewerKeySuffix);
  useEffect(() => {
    if (viewerKeySuffix !== viewerKey) {
      setMeasuredVersion(measuredVersion + 1);
      setViewerKey(viewerKeySuffix);
    }
  }, [viewerKeySuffix, viewerKey, measuredVersion]);

  const containerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const measuredCookieType = getMeasuredCookieType(zoneName, module?.position);
  const measuredContainer = useMeasuredCookie(
    measuredCookieType,
    containerRef,
    {
      cookieDim: 'width',
      listenOnWindow: true,
      sentinelRef,
      version: measuredVersion,
    }
  );

  const width = measuredContainer?.width || 600;

  return (
    <>
      <div ref={sentinelRef} style={{ maxWidth: '100%' }} />
      <Background
        variant="custombox"
        className={c('da-editor-custombox', s['root'])}
        contentClassName={s['content']}
        {...backgroundProps}
        noPadding={noPadding}
      >
        <div ref={containerRef} style={{ width: '100%' }}>
          <Suspense fallback={null}>
            <TextRenderer
              width={width}
              textContent={customtext}
              reactViewer={CustomBoxViewer}
              legacyTheme={'default'}
              viewerKey={`custombox-${moduleId}-${moduleVersion}-${width}`}
            />
          </Suspense>
        </div>
      </Background>
    </>
  );
};
Custom.displayName = 'Custom';

export default Custom;
